import { prefersReducedMotion, RUNTIME_ROOT_SELECTOR } from './dom.js';

const COUNT_UP_ROOT_SELECTOR = '[data-bp-count-up]';
const TARGET_CLASS_ATTRIBUTE = 'data-bp-count-target';
const DURATION_ATTRIBUTE = 'data-bp-count-duration';
const READY_ATTRIBUTE = 'data-bp-ready';
const DEFAULT_DURATION = 1100;
const MAX_DURATION = 10_000;
const FONT_READY_TIMEOUT = 1500;
const INTERSECTION_THRESHOLD = 0.4;
const COUNT_PATTERN = /^((?:\d{1,3}(?:,\d{3})+)|\d+)(\+?)$/;
const DURATION_PATTERN = /^[1-9]\d*$/;
const CLASS_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_-]*$/;

function findCapabilityRoots(runtimeRoot) {
  if (!runtimeRoot.matches?.(RUNTIME_ROOT_SELECTOR)) return [];

  return Array.from(runtimeRoot.querySelectorAll(COUNT_UP_ROOT_SELECTOR))
    .filter((root) => root.closest(RUNTIME_ROOT_SELECTOR) === runtimeRoot);
}

function isOwnedLeaf(element, capabilityRoot, runtimeRoot) {
  return element.childElementCount === 0
    && element.closest(COUNT_UP_ROOT_SELECTOR) === capabilityRoot
    && element.closest(RUNTIME_ROOT_SELECTOR) === runtimeRoot;
}

function parseTarget(element, capabilityRoot, runtimeRoot) {
  if (!isOwnedLeaf(element, capabilityRoot, runtimeRoot)) return null;

  const authoredText = element.textContent;
  const match = authoredText.trim().match(COUNT_PATTERN);
  if (!match) return null;

  const value = Number(match[1].replaceAll(',', ''));
  if (!Number.isSafeInteger(value)) return null;

  return {
    authoredText,
    element,
    grouped: match[1].includes(','),
    suffix: match[2],
    value,
  };
}

function findTargets(capabilityRoot, runtimeRoot) {
  const targetClass = capabilityRoot.getAttribute(TARGET_CLASS_ATTRIBUTE);
  if (!CLASS_NAME_PATTERN.test(targetClass ?? '')) return [];

  return Array.from(capabilityRoot.getElementsByClassName(targetClass))
    .map((element) => parseTarget(element, capabilityRoot, runtimeRoot))
    .filter((target) => target !== null);
}

function readDuration(capabilityRoot) {
  const authoredDuration = capabilityRoot.getAttribute(DURATION_ATTRIBUTE);
  if (authoredDuration === null || !DURATION_PATTERN.test(authoredDuration)) {
    return DEFAULT_DURATION;
  }

  const duration = Number(authoredDuration);
  return Number.isSafeInteger(duration) && duration <= MAX_DURATION
    ? duration
    : DEFAULT_DURATION;
}

function formatValue(value, grouped) {
  const rounded = String(Math.round(value));
  return grouped ? rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : rounded;
}

// Digits are proportional, so measuring waits for the real face to avoid pinning a fallback-font width.
function whenMeasurable(environment) {
  const fontsReady = environment.document?.fonts?.ready;
  if (typeof fontsReady?.then !== 'function') return null;
  if (typeof environment.setTimeout !== 'function') return Promise.resolve(fontsReady);

  return Promise.race([
    fontsReady,
    new Promise((resolve) => environment.setTimeout(resolve, FONT_READY_TIMEOUT)),
  ]);
}

// getBoundingClientRect reports visual px that `zoom` has already scaled, so divide the zoom out before writing a layout width.
function readZoom(environment) {
  const body = environment.document?.body;
  if (!body || typeof environment.getComputedStyle !== 'function') return 1;

  const zoom = Number.parseFloat(environment.getComputedStyle(body).zoom);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

function animateTarget(target, duration, environment, capabilityRoot, runtimeRoot) {
  const previousWidth = target.element.style.width;
  let lastWrittenText = target.authoredText;
  let startedAt;

  const restoreWidth = () => {
    target.element.style.width = previousWidth;
  };
  const canWrite = () => isOwnedLeaf(target.element, capabilityRoot, runtimeRoot)
    && target.element.textContent === lastWrittenText;
  const update = (timestamp) => {
    if (!canWrite()) {
      restoreWidth();
      return;
    }

    if (startedAt === undefined) startedAt = timestamp;
    const progress = Math.min(1, Math.max(0, (timestamp - startedAt) / duration));
    if (progress >= 1) {
      target.element.textContent = target.authoredText;
      lastWrittenText = target.authoredText;
      restoreWidth();
      return;
    }

    const easedProgress = 1 - (1 - progress) ** 3;
    lastWrittenText = `${formatValue(target.value * easedProgress, target.grouped)}${target.suffix}`;
    target.element.textContent = lastWrittenText;
    environment.requestAnimationFrame(update);
  };
  const start = () => {
    if (!canWrite()) return;

    // The target still renders its authored final value, so this is the final-value width.
    const layoutWidth = target.element.getBoundingClientRect().width / readZoom(environment);
    target.element.style.width = `${layoutWidth}px`;
    environment.requestAnimationFrame(update);
  };

  const measurable = whenMeasurable(environment);
  if (measurable === null) {
    start();
    return;
  }

  measurable.then(start, start);
}

export function initializeCountUps(runtimeRoot, environment = globalThis) {
  const shouldAnimate = typeof environment.IntersectionObserver === 'function'
    && typeof environment.requestAnimationFrame === 'function'
    && !prefersReducedMotion(environment);
  let initializedCount = 0;

  for (const capabilityRoot of findCapabilityRoots(runtimeRoot)) {
    if (capabilityRoot.getAttribute(READY_ATTRIBUTE) === 'count-up') continue;

    const targets = findTargets(capabilityRoot, runtimeRoot);
    if (!targets.length) continue;

    if (shouldAnimate) {
      const duration = readDuration(capabilityRoot);
      const targetByElement = new Map(targets.map((target) => [target.element, target]));
      const startedTargets = new WeakSet();
      const observer = new environment.IntersectionObserver((entries) => {
        for (const entry of entries) {
          const target = targetByElement.get(entry.target);
          if (
            !entry.isIntersecting
            || !(entry.intersectionRatio >= INTERSECTION_THRESHOLD)
            || !target
            || startedTargets.has(entry.target)
          ) {
            continue;
          }

          startedTargets.add(entry.target);
          observer.unobserve(entry.target);
          const currentTarget = parseTarget(entry.target, capabilityRoot, runtimeRoot);
          if (!currentTarget || currentTarget.authoredText !== target.authoredText) continue;
          animateTarget(currentTarget, duration, environment, capabilityRoot, runtimeRoot);
        }
      }, { threshold: INTERSECTION_THRESHOLD });

      for (const target of targets) observer.observe(target.element);
    }

    capabilityRoot.setAttribute(READY_ATTRIBUTE, 'count-up');
    initializedCount += 1;
  }

  return initializedCount;
}
